import React from 'react'
import { normalizeTime24, sanitizeTimeTyping, autoInsertColon } from '../../utils/timeFormat'

/**
 * Time24Input — input de hora en formato 24h escribible (HH:MM).
 *
 * Reemplaza al <input type="time"> nativo, cuyo formato (AM/PM vs 24h) depende de la
 * REGION del sistema operativo del agente y no se puede forzar de forma confiable.
 * Este componente SIEMPRE trabaja en 24h: el agente escribe "10:00", "13:00", "17:15"
 * (o "9", "930", "1715") y se emite un valor canonico "HH:MM" via onChange, o "" si la
 * hora todavia no es valida. Asi el resto del flujo (next_followup_date, linea AS) no
 * cambia respecto al input nativo, que tambien entregaba "HH:MM" 24h.
 *
 * Props:
 *   value: string canonico "HH:MM" | ''   (controlado por el padre)
 *   onChange(canonical: string): void      (recibe "HH:MM" o '')
 *   className, placeholder, disabled, autoFocus
 */
export default function Time24Input({
  value = '',
  onChange = () => {},
  className = '',
  placeholder = 'HH:MM',
  disabled = false,
  autoFocus = false,
}) {
  // display = lo que ve/escribe el agente (texto crudo). value = canonico del padre.
  const [display, setDisplay] = React.useState(value || '')

  // Ultimo canonico que ESTE input emitio via onChange. Permite distinguir el ECO del
  // padre (value vuelve identico a lo que emitimos, incluido '') de un cambio externo
  // real (restaurar borrador, atajo). Sin esto, tipear un estado intermedio invalido
  // ("12:", "93", "171") emitia '' y el eco del padre BORRABA el texto en pleno tipeo
  // (bug: el campo se vaciaba al escribir los minutos).
  const lastEmittedRef = React.useRef(value || '')

  // Sincroniza el display solo ante cambios externos reales del value (restaurar
  // borrador, set programatico del padre). El eco de nuestro propio onChange nunca
  // toca el display: el agente sigue escribiendo. Nota: ningun caller resetea asTime
  // a '' con el input montado (los reset ocurren con el modal cerrado y el input se
  // remonta al abrir), asi que no se necesita guarda especial para ese caso.
  React.useEffect(() => {
    if (value === lastEmittedRef.current) return // eco de nuestro propio onChange
    lastEmittedRef.current = value || ''
    setDisplay(value || '')
  }, [value])

  const emit = (canonical) => {
    lastEmittedRef.current = canonical
    onChange(canonical)
  }

  const handleChange = (e) => {
    // autoInsertColon: orden visual — si el agente tipea solo digitos ("1615", "930"),
    // el ":" se inserta solo cuando la interpretacion es inequivoca (nunca reordena).
    const typed = autoInsertColon(sanitizeTimeTyping(e.target.value))
    setDisplay(typed)
    emit(normalizeTime24(typed)) // '' mientras la entrada sea parcial/invalida
  }

  const handleBlur = () => {
    const canonical = normalizeTime24(display)
    if (canonical) {
      setDisplay(canonical) // ordena el display al canonico (09:00, 13:00)
      emit(canonical)
    } else if (display !== '') {
      // entrada invalida (p.ej. "25") -> limpiar para no dejar basura
      setDisplay('')
      emit('')
    }
  }

  return (
    <input
      type="text"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      maxLength={5}
      className={className}
    />
  )
}
